import { useState } from 'react';
import { Box, Tab, Tabs, Typography, useTheme } from '@mui/material';
import Button from 'components/Common/Button';
import AgentConfig from './components/AgentConfig';
import CustomizeBehavior from './components/CustomizeBehavior';
import StoreInfo from './components/StoreInfo';
import { useForm, FormProvider } from 'react-hook-form';
import { useGetAgent, useUpdateAgent } from 'apis/business/agent/hook';
import Spinner from 'components/Common/Spinner';
import { ScrollToTop } from 'App';
import { zodResolver } from '@hookform/resolvers/zod';
import { agentConfigSchema, customizeBehaviorSchema, storeInfoSchema } from 'utils/schema/validator/AgentValidation';
// import { useGetVoices } from 'apis/business/agent/voice/hook';
import { a11yProps, CustomTabPanel } from 'components/Common/CustomTabPanel';
import SupportAgentIcon from 'assets/icons/SupportAgentIcon';
import PsychologyIcon from 'assets/icons/PsychologyIcon';
import StoreIcon from 'assets/icons/StoreIcon';
import { useLocation, useNavigate } from 'react-router-dom';

export default function Identity() {
  const navigate = useNavigate();
  const location = useLocation();

  const getTabIndexFromUrl = () => {
    const params = new URLSearchParams(location.search);
    const tabIndex = params.get('tab');
    return tabIndex ? Number(tabIndex) : 0;
  };

  const theme = useTheme();
  const [skipped, setSkipped] = useState(new Set<number>());
  const [value, setValue] = useState<number>(getTabIndexFromUrl);

  const { data: agent, isLoading: isLoadingAgentLoading } = useGetAgent();

  const { mutateAsync: updateAgent } = useUpdateAgent();

  const methods = useForm({
    values: { agent },
    mode: 'onChange',
    resolver: zodResolver(schemas[value])
  });

  const isStepSkipped = (step: number) => {
    return skipped.has(step);
  };

  const handleNext = async () => {
    const result = await methods.trigger();
    if (!result) return;

    let newSkipped = skipped;
    if (isStepSkipped(value)) {
      newSkipped = new Set(newSkipped.values());
      newSkipped.delete(value);
    }
    const data = methods.getValues();
    await updateAgent(data);

    setValue((prevActiveStep) => prevActiveStep + 1);
    setSkipped(newSkipped);
  };

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    navigate({
      pathname: location.pathname,
      search: `?tab=${newValue}`
    });
  };

  const onSubmit = (data: any) => {
    updateAgent(data);
  };

  const isLoading = isLoadingAgentLoading;

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <Box>
      <ScrollToTop state={value} />
      <Box position='sticky' top='65px' bgcolor={theme.palette.background.default} pb={0.5} zIndex={1000}>
        <Typography variant='h6'>Get Started</Typography>
        <Typography color='secondary' variant='body2'>
          Create your customer service agent in three steps: configuration, behaviour, store info
        </Typography>
        <Box bgcolor='background.paper' mt={2} py={0.5} px={1}>
          <Tabs value={value} onChange={handleChange} aria-label='configure tabs'>
            <Tab
              label={
                <Box display='flex' gap={1}>
                  <SupportAgentIcon />
                  Agent configuration
                </Box>
              }
              {...a11yProps('configure-tab', 'configure-tabpanel', 0)}
            />
            <Tab
              label={
                <Box display='flex' gap={1}>
                  <PsychologyIcon />
                  Customize Behavior
                </Box>
              }
              {...a11yProps('configure-tab', 'configure-tabpanel', 1)}
            />
            <Tab
              label={
                <Box display='flex' gap={1}>
                  <StoreIcon />
                  Store Info
                </Box>
              }
              {...a11yProps('configure-tab', 'configure-tabpanel', 2)}
            />
          </Tabs>
        </Box>
      </Box>

      <FormProvider {...methods}>
        <Box component='form' onSubmit={methods.handleSubmit(onSubmit)}>
          <CustomTabPanel value={value} id='configure-tab' ariaControl='configure-tabpanel' index={0}>
            <AgentConfig />
          </CustomTabPanel>
          <CustomTabPanel value={value} id='configure-tab' ariaControl='configure-tabpanel' index={1}>
            <CustomizeBehavior />
          </CustomTabPanel>
          <CustomTabPanel value={value} id='configure-tab' ariaControl='configure-tabpanel' index={2}>
            <StoreInfo />
          </CustomTabPanel>
        </Box>
        <Box display='flex' justifyContent='end' mt={2}>
          <Button onClick={value === 2 ? methods.handleSubmit(onSubmit) : handleNext}>Save</Button>
        </Box>
      </FormProvider>
    </Box>
  );
}

const schemas = [agentConfigSchema, customizeBehaviorSchema, storeInfoSchema];
